import React, { useEffect, useState, useMemo } from 'react';
import { Dumbbell, Plus, Edit, Trash2, X, Save, Video, Search, Filter, DownloadCloud } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MUSCLE_GROUPS = [
  'gluteos', 'cuadriceps', 'isquios', 'aductores', 'abductores', 'pantorrillas',
  'abdomen', 'lumbares', 'espalda', 'hombros', 'brazos', 'triceps', 'pecho'
];
const DIVISIONS = ['inferior', 'superior', 'core'];
const TYPES = ['activacion_inferior', 'activacion_superior', 'principal', 'complementario'];
const LEVELS = ['principiante', 'intermedio', 'avanzado'];
const LOCATIONS = ['casa', 'gimnasio', 'ambos'];

const emptyForm = {
  name: '',
  video_url: '',
  muscle_group: 'gluteos',
  division: 'inferior',
  exercise_type: 'principal',
  levels: ['principiante'],
  location: 'ambos',
  is_priority: false,
  series_templates_text: '',
  technique_id: '',
  description: '',
  tags: []
};

const cap = (s) => (s || '').replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

const AdminLibraryExercises = () => {
  const [exercises, setExercises] = useState([]);
  const [techniques, setTechniques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterLocation, setFilterLocation] = useState('todos');
  const [filterMuscle, setFilterMuscle] = useState('todos');
  const [migrating, setMigrating] = useState(false);

  const token = localStorage.getItem('session_token') || localStorage.getItem('token');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [exRes, techRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/library-exercises`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/training-techniques`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setExercises(exRes.data.items || []);
      setTechniques(techRes.data.items || []);
    } catch (err) {
      toast.error('Error al cargar la biblioteca');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (ex) => {
    setEditing(ex);
    // Normalizar levels: si tiene array usarlo, si no usar el single level legacy
    const lvls = (Array.isArray(ex.levels) && ex.levels.length > 0) ? ex.levels : (ex.level ? [ex.level] : ['principiante']);
    // Convertir series_templates (array de arrays/strings) a texto multilinea con separadores de plantilla
    let stText = '';
    const st = ex.series_templates;
    if (Array.isArray(st) && st.length) {
      stText = st.map(t => {
        if (Array.isArray(t)) return t.join('\n');
        return String(t || '');
      }).join('\n\n');
    }
    setForm({
      ...emptyForm,
      ...ex,
      levels: lvls,
      technique_id: ex.technique_id || '',
      tags: ex.tags || [],
      series_templates_text: stText
    });
    setModalOpen(true);
  };

  const toggleLevel = (lvl) => {
    setForm(prev => {
      const has = prev.levels.includes(lvl);
      let next = has ? prev.levels.filter(l => l !== lvl) : [...prev.levels, lvl];
      if (next.length === 0) next = [lvl]; // al menos uno
      return { ...prev, levels: next };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      // Convertir texto de plantillas a array de arrays
      const stText = (form.series_templates_text || '').trim();
      let series_templates = [];
      if (stText) {
        // Separar por línea en blanco → cada bloque es una plantilla
        const blocks = stText.split(/\n\s*\n/);
        for (const block of blocks) {
          const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
          if (lines.length) series_templates.push(lines);
        }
      }
      const { series_templates_text, ...rest } = form;
      const payload = {
        ...rest,
        series_templates,
        technique_id: form.technique_id || null
      };
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/admin/library-exercises/${editing.exercise_id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Ejercicio actualizado');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/library-exercises`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Ejercicio creado');
      }
      setModalOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ex) => {
    if (!window.confirm(`¿Eliminar "${ex.name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/library-exercises/${ex.exercise_id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Ejercicio eliminado');
      fetchAll();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleMigrateVideos = async () => {
    if (!window.confirm('Esto importará los videos de las rutinas viejas (Nivel 1, 2, 3 y Premium) a esta biblioteca. ¿Continuar?')) return;
    setMigrating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/library-exercises/migrate-from-program-content`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en la migración');
    } finally {
      setMigrating(false);
    }
  };

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return exercises.filter(ex => {
      if (term && !(ex.name || '').toLowerCase().includes(term)) return false;
      if (filterLocation !== 'todos' && ex.location !== filterLocation) return false;
      if (filterMuscle !== 'todos' && ex.muscle_group !== filterMuscle) return false;
      return true;
    });
  }, [exercises, search, filterLocation, filterMuscle]);

  const locationBadge = (loc) => {
    const map = {
      casa: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      gimnasio: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      ambos: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
    };
    return map[loc] || map.ambos;
  };

  return (
    <div className="min-h-screen bg-[#09090b] p-6 md:p-10" data-testid="admin-library-exercises">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-2xl bg-pink-500/15 border border-pink-500/30 flex items-center justify-center">
                <Dumbbell className="w-6 h-6 text-pink-400" />
              </div>
              <h1 className="font-barlow text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
                Biblioteca de <span className="text-pink-500">Ejercicios</span>
              </h1>
            </div>
            <p className="text-zinc-400 text-sm">{exercises.length} ejercicios en total</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleMigrateVideos} disabled={migrating} variant="outline" className="border-pink-500/40 text-pink-300 hover:bg-pink-500/10 rounded-full px-5 py-5 font-bold uppercase text-xs" data-testid="migrate-videos-btn" title="Importar videos de Nivel 1/2/3/Premium">
              <DownloadCloud className="w-4 h-4 mr-2" /> {migrating ? 'Migrando...' : 'Importar videos viejos'}
            </Button>
            <Button onClick={openCreate} className="bg-pink-600 hover:bg-pink-500 rounded-full px-6 py-5 font-bold uppercase text-sm" data-testid="add-exercise-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo ejercicio
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre..."
              className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-full text-white text-sm focus:border-pink-500 outline-none"
            />
          </div>
          <select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)} className="px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-full text-white text-sm focus:border-pink-500 outline-none">
            <option value="todos">Todos los lugares</option>
            {LOCATIONS.map(l => <option key={l} value={l}>{cap(l)}</option>)}
          </select>
          <select value={filterMuscle} onChange={(e) => setFilterMuscle(e.target.value)} className="px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-full text-white text-sm focus:border-pink-500 outline-none">
            <option value="todos">Todos los músculos</option>
            {MUSCLE_GROUPS.map(m => <option key={m} value={m}>{cap(m)}</option>)}
          </select>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-20 text-zinc-500">Cargando ejercicios...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-3xl p-12 text-center">
            <Dumbbell className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400 mb-1">No hay ejercicios todavía</p>
            <p className="text-zinc-600 text-sm">Crea el primer ejercicio para comenzar tu biblioteca.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(ex => (
              <div key={ex.exercise_id} className="bg-zinc-900/60 border border-zinc-800 hover:border-pink-500/40 rounded-2xl p-5 transition-colors" data-testid={`exercise-card-${ex.exercise_id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white text-base truncate">{ex.name}</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">{cap(ex.muscle_group)} · {cap(ex.division)}</p>
                  </div>
                  {ex.video_url && (
                    <a href={ex.video_url} target="_blank" rel="noreferrer" className="text-pink-400 hover:text-pink-300 flex-shrink-0" title="Ver video">
                      <Video className="w-5 h-5" />
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {ex.is_priority && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-pink-500/15 text-pink-300 border border-pink-500/40">★ Base</span>
                  )}
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${locationBadge(ex.location)}`}>{cap(ex.location)}</span>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">{cap(ex.exercise_type)}</span>
                  {((ex.levels && ex.levels.length > 0) ? ex.levels : [ex.level]).filter(Boolean).map(lvl => (
                    <span key={lvl} className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">{cap(lvl)}</span>
                  ))}
                </div>
                <div className="text-xs text-zinc-400 mb-4">
                  <span className="text-zinc-500">Plantillas:</span> {ex.series_templates?.length || 0}
                </div>
                <div className="flex items-center gap-2 pt-3 border-t border-zinc-800">
                  <button onClick={() => openEdit(ex)} className="flex-1 text-xs font-bold uppercase text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-full py-2 transition-colors flex items-center justify-center gap-1.5" data-testid={`edit-${ex.exercise_id}`}>
                    <Edit className="w-3.5 h-3.5" /> Editar
                  </button>
                  <button onClick={() => handleDelete(ex)} className="flex-1 text-xs font-bold uppercase text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-full py-2 transition-colors flex items-center justify-center gap-1.5" data-testid={`delete-${ex.exercise_id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-zinc-800">
              <h2 className="text-xl font-bold text-white">
                {editing ? 'Editar ejercicio' : 'Nuevo ejercicio'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">Nombre del ejercicio *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej. Hip Thrust con barra"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-pink-500 outline-none"
                  data-testid="exercise-name-input"
                />
              </div>

              {/* Video URL */}
              <div>
                <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">URL del video (Google Drive)</label>
                <input
                  type="url"
                  value={form.video_url}
                  onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                  placeholder="https://drive.google.com/file/d/.../view"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-pink-500 outline-none"
                />
                <p className="text-[11px] text-zinc-500 mt-1">Asegúrate que el enlace esté con permiso "Cualquiera con el enlace puede ver"</p>
              </div>

              {/* Grid de selects */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">Grupo muscular</label>
                  <select value={form.muscle_group} onChange={(e) => setForm({ ...form, muscle_group: e.target.value })} className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-pink-500 outline-none">
                    {MUSCLE_GROUPS.map(m => <option key={m} value={m}>{cap(m)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">División</label>
                  <select value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value })} className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-pink-500 outline-none">
                    {DIVISIONS.map(d => <option key={d} value={d}>{cap(d)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">Tipo</label>
                  <select value={form.exercise_type} onChange={(e) => setForm({ ...form, exercise_type: e.target.value })} className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-pink-500 outline-none">
                    {TYPES.map(t => <option key={t} value={t}>{cap(t)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">Niveles (multi-selección)</label>
                  <div className="flex flex-wrap gap-2">
                    {LEVELS.map(l => {
                      const sel = form.levels.includes(l);
                      return (
                        <button
                          key={l}
                          type="button"
                          onClick={() => toggleLevel(l)}
                          className={`px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-wider border transition-all ${sel ? 'bg-pink-500 border-pink-500 text-white' : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-pink-400'}`}
                          data-testid={`level-toggle-${l}`}
                        >
                          {cap(l)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">Lugar de entrenamiento</label>
                  <select value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-pink-500 outline-none">
                    {LOCATIONS.map(l => <option key={l} value={l}>{cap(l)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">Técnica sugerida</label>
                  <select value={form.technique_id || ''} onChange={(e) => setForm({ ...form, technique_id: e.target.value })} className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-pink-500 outline-none">
                    <option value="">Ninguna</option>
                    {techniques.map(t => <option key={t.technique_id} value={t.technique_id}>{t.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Toggle de ejercicio base/prioritario */}
              <div className="bg-pink-500/5 border border-pink-500/30 rounded-xl p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.is_priority}
                    onChange={(e) => setForm({ ...form, is_priority: e.target.checked })}
                    className="mt-1 w-5 h-5 accent-pink-500"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-white">Ejercicio base / prioritario</div>
                    <p className="text-[11px] text-zinc-400 mt-1">Si marcas esta opción, este ejercicio SIEMPRE aparecerá en las rutinas cuyo día contenga su grupo muscular. Úsalo para ejercicios "moduladores" como Hip Thrust en glúteos, Extensión de rodilla en cuádriceps, Flexor en isquios, etc.</p>
                  </div>
                </label>
              </div>

              {/* Plantillas de series (lo que verá la usuaria en su rutina) */}
              <div>
                <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">Plantillas de series</label>
                <p className="text-[11px] text-zinc-500 mb-2">Una línea = una serie. Separa varias plantillas con una línea en blanco. El generador escogerá una al azar para variar entre rutinas. Si lo dejas vacío, se usarán series genéricas.</p>
                <textarea
                  value={form.series_templates_text}
                  onChange={(e) => setForm({ ...form, series_templates_text: e.target.value })}
                  placeholder={`1x 20 reps de aproximación con peso ligero\n1x 15 reps subiendo peso y controlando bajada\n1x 12 reps con peso de trabajo\n1x 8 reps cluster: 8 reps - descansa 12s - 8 reps - descansa 12s - 8 reps\n\n1x 20 reps de aproximación\n1x 15 reps más pesadas\n2x 12 reps pesadas, descansas 12s y sacas una tanda más`}
                  rows={8}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-pink-500 outline-none resize-none font-mono text-xs"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-zinc-400 uppercase mb-2 tracking-wider">Descripción / técnica corta</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Posición, ejecución, tip clave..."
                  rows={3}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-pink-500 outline-none resize-none"
                />
              </div>
            </div>

            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setModalOpen(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-full px-6">
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-pink-600 hover:bg-pink-500 rounded-full px-8" data-testid="save-exercise-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLibraryExercises;
